import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, CaretDown, ArrowRight, ShoppingBag } from 'phosphor-react';
import { motion, AnimatePresence } from 'framer-motion';
import SBack from '@/components/ui/molecules/SBack';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import SProductCard from '@/components/ui/organisms/SProductCard';
import { SkeletonLoader } from '@/components/ui/molecules/SkeletonLoader';
import SDialog from '@/components/ui/molecules/SDialog';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useLocations } from '@/hooks/useLocations';
import { useProducts } from '@/hooks/useProducts';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useCart } from '@/context/CartContext';
import { useToast } from '@/context/ToastContext';
import { CartItem } from '@/types/cart.types';
import './Shop.css';

const SELECTED_LOCATION_KEY = 'teebox_selected_location';

const Shop: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // QR-code deep link: /shop?location=<id> takes priority over the
  // last-selected location, so scanning a location's QR code always lands
  // on that location rather than wherever the user shopped last.
  const locationParam = searchParams.get('location');
  const { location: userLocation } = useUserLocation();
  const { locations } = useLocations();

  const getInitialLocation = () => {
    if (locationParam && locations.length > 0) {
      const found = locations.find(l => l.id === locationParam);
      if (found) return found as any;
    }
    const storedId = localStorage.getItem(SELECTED_LOCATION_KEY);
    if (storedId && locations.length > 0) {
      const found = locations.find(l => l.id === storedId);
      if (found) return found as any;
    }
    return userLocation;
  };

  const [selectedLocation, setSelectedLocation] = useState(getInitialLocation);
  const selectedLocationId = selectedLocation?.id;

  useEffect(() => {
    if (locations.length === 0) return;

    if (locationParam) {
      const found = locations.find(l => l.id === locationParam);
      if (found) {
        setSelectedLocation(found as any);
        return;
      }
      // Unknown/inaccessible location id in the URL — fall through to the
      // usual localStorage/home-location resolution below.
    }

    const storedId = localStorage.getItem(SELECTED_LOCATION_KEY);
    if (storedId) {
      const found = locations.find(l => l.id === storedId);
      if (found) setSelectedLocation(found as any);
    }
  }, [locations, locationParam]);

  useEffect(() => {
    if (selectedLocation) localStorage.setItem(SELECTED_LOCATION_KEY, selectedLocation.id);
  }, [selectedLocation]);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  // Search and collection go to the API, not a local filter: the grid holds
  // only the pages loaded so far, so filtering here searched a prefix of the
  // catalogue and reported no matches for everything below it.
  const {
    products: apiProducts,
    collections: productCollections,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    getProduct,
    error,
  } = useProducts(selectedLocationId, {
    search: searchQuery,
    collection: activeFilter === 'All' ? undefined : activeFilter,
  });
  const isFiltered = searchQuery.trim().length > 0 || activeFilter !== 'All';
  const sentinelRef = useInfiniteScroll<HTMLDivElement>(loadMore, {
    enabled: hasMore,
    watch: apiProducts.length,
  });
  const [showClearCartDialog, setShowClearCartDialog] = useState(false);
  const [showLocationMenu, setShowLocationMenu] = useState(false);
  const { cart, addToCart, removeFromCart, updateQuantity, clearCart, getCartQuantity, getTotalCartItems } = useCart();
  const { showToast } = useToast();

  const handleLocationChange = (location: any) => {
    setSelectedLocation(location);
    setShowLocationMenu(false);
    if (cart.length > 0) {
      clearCart();
      showToast({ variant: 'success', title: 'Location Changed', message: 'Cart cleared — products vary by location', duration: 3000 });
    }
  };

  const handleViewCart = () => {
    const cartItems: CartItem[] = cart.map(item => {
      const product = getProduct(item.productId);
      return {
        productId: item.productId,
        name: product?.name || '',
        quantity: item.quantity,
        price: product ? product.price : 0,
        specification: product?.description || '',
        image: product?.image,
      };
    });
    navigate('/shop-cart', { state: { cart: cartItems, location: selectedLocation } });
  };

  const totalItems = getTotalCartItems();
  const cartTotal = cart.reduce((sum, item) => {
    const product = getProduct(item.productId);
    return sum + (product ? product.price * item.quantity : 0);
  }, 0);

  return (
    <div className="shop" onClick={() => showLocationMenu && setShowLocationMenu(false)}>
      {/* Header */}
      <div className="shop__header">
        <SBack onClick={() => navigate('/home')} />
        <h1 className="shop__title">Shop</h1>
        <ThemeToggle />
      </div>

      {/* Search */}
      <div className="shop__search">
        <input
          className="shop__search-input"
          placeholder="Search the shelf..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Location selector */}
      <div className="shop__location" onClick={e => e.stopPropagation()}>
        <MapPin size={14} color="#c99535" weight="fill" />
        <button className="shop__location-btn" onClick={() => setShowLocationMenu(v => !v)}>
          <span>{selectedLocation?.name || 'Select Location'}</span>
          <CaretDown size={12} color="#6e6a60" />
        </button>

        <AnimatePresence>
          {showLocationMenu && (
            <motion.div
              className="shop__location-menu"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
            >
              {locations.map(loc => (
                <div
                  key={loc.id}
                  className={`shop__location-item ${selectedLocation?.id === loc.id ? 'shop__location-item--active' : ''}`}
                  onClick={() => handleLocationChange(loc)}
                >
                  <span>{loc.name}</span>
                  {selectedLocation?.id === loc.id && <span className="shop__location-check">✓</span>}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="shop__state"><SkeletonLoader /></div>
      ) : error ? (
        <div className="shop__state">Failed to load products. Please try again.</div>
      ) : apiProducts.length === 0 && !isFiltered ? (
        <div className="shop__state">
          <ShoppingBag size={44} weight="thin" color="var(--tbx-text-muted)" />
          <p className="shop__state-title">Nothing here yet</p>
          <p className="shop__state-sub">No products available at this location right now. Check back soon.</p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="shop__filters">
            {['All', ...productCollections].map(col => (
              <button
                key={col}
                className={`shop__filter-pill ${activeFilter === col ? 'shop__filter-pill--active' : ''}`}
                onClick={() => setActiveFilter(col)}
              >
                {col}
              </button>
            ))}
          </div>

          {/* Section label */}
          {/* <span className="shop__section-label">Products</span> */}

          {/* Grid */}
          <div className="shop__grid">
            {apiProducts.length === 0 ? (
              <div className="shop__state" style={{ gridColumn: '1 / -1' }}>No products found</div>
            ) : (
              apiProducts.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22, delay: i * 0.04, ease: 'easeOut' }}
                >
                  <SProductCard
                    id={product.id}
                    name={product.name}
                    quantity={product.quantity || ''}
                    price={product.price}
                    image={product.image}
                    cartQuantity={getCartQuantity(product.id)}
                    locationId={selectedLocationId}
                    onAdd={addToCart}
                    onRemove={removeFromCart}
                    onQuantityChange={updateQuantity}
                  />
                </motion.div>
              ))
            )}
          </div>

          {/* Pages load on scroll. The sentinel sits below the grid so it
              spans the full width instead of taking a card slot, and keeps
              its height either way so the page does not jump. */}
          {hasMore && (
            <div ref={sentinelRef} className="shop__load-more">
              {isLoadingMore && 'Loading more…'}
            </div>
          )}
        </>
      )}

      {/* Floating cart bar */}
      <AnimatePresence>
        {totalItems > 0 && (
          <motion.div
            className="shop__cart-bar"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <div className="shop__cart-main" onClick={handleViewCart}>
              <div className="shop__cart-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="3" y1="6" x2="21" y2="6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M16 10a4 4 0 01-8 0" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="shop__cart-info">
                <span className="shop__cart-label">Cart</span>
                <span className="shop__cart-summary">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
              </div>
            </div>

            <div className="shop__cart-cta" onClick={handleViewCart}>
              <span>${cartTotal.toFixed(2)}</span>
              <ArrowRight size={14} weight="bold" />
            </div>

            <div className="shop__cart-divider" />

            <button
              className="shop__cart-clear"
              onClick={e => { e.stopPropagation(); setShowClearCartDialog(true); }}
              title="Clear cart"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <SDialog
        isOpen={showClearCartDialog}
        onClose={() => setShowClearCartDialog(false)}
        title="Clear your entire cart?"
        onConfirm={() => { clearCart(); setShowClearCartDialog(false); }}
        confirmText="Yes, clear"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Shop;
